import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import {
  getCommunityProfileRequest,
  updateCommunityProfileRequest as updateProfileApi,
  uploadCommunityAvatarRequest,
  getGroupsRequest,
  getMemberGroupsRequest,
  getNotificationsRequest,
  markNotificationReadApiRequest,
  markAllNotificationsReadApiRequest,
  deleteNotificationApiRequest,
  deleteAllNotificationsApiRequest,
} from '../../../api/community';
import {
  createNotification,
  NOTIFICATION_TYPE,
  SUCCESS_MESSAGES,
} from '../../../services/Notification';
import {
  FETCH_COMMUNITY_PROFILE_REQUEST,
  UPDATE_COMMUNITY_PROFILE_REQUEST,
  UPDATE_COMMUNITY_PROFILE_PIC_REQUEST,
  FETCH_SUGGESTED_GROUPS_REQUEST,
  FETCH_MY_GROUPS_REQUEST,
  FETCH_NOTIFICATIONS_REQUEST,
  MARK_NOTIFICATION_READ_REQUEST,
  MARK_ALL_NOTIFICATIONS_READ_REQUEST,
  DELETE_NOTIFICATION_REQUEST,
  DELETE_ALL_NOTIFICATIONS_REQUEST,
} from '../../constant/community';
import {
  fetchCommunityProfileRequest,
  fetchCommunityProfileSuccess,
  fetchCommunityProfileFailure,
  updateCommunityProfileSuccess,
  updateCommunityProfileFailure,
  communitySetIsLoading,
  fetchSuggestedGroupsSuccess,
  fetchSuggestedGroupsFailure,
  fetchMyGroupsSuccess,
  fetchMyGroupsFailure,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  removeNotification,
  updateNotificationRead,
  updateAllNotificationsRead,
  clearAllNotifications,
} from '../../action/community';
import { LOCAL_STORAGE_CONSTANT } from '../../../constants/app';

const ONBOARDING_KEY = LOCAL_STORAGE_CONSTANT.COMMUNITY_ONBOARDING;
const COMMUNITY_UID_KEY = LOCAL_STORAGE_CONSTANT.COMMUNITY_UID;

// Syncs API profile into the wizard's localStorage entry.
// When apiWins=true (after a successful update), the API response takes priority.
// When apiWins=false (profile fetch), non-empty local values take priority so
// any in-progress wizard draft typed before the fetch isn't clobbered.
// Also caches the community uid so member-scoped endpoints can use it without refetching.
export function syncProfileToStorage(profile, apiWins = false) {
  if (!profile) return;
  try {
    // The API is the source of truth: replace a uid left behind by a
    // previous account on this browser instead of keeping it.
    if (
      profile.uid &&
      localStorage.getItem(COMMUNITY_UID_KEY) !== String(profile.uid)
    ) {
      localStorage.setItem(COMMUNITY_UID_KEY, profile.uid);
    }
    const raw = localStorage.getItem(ONBOARDING_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    const apiData = {
      name: profile.name || '',
      location: profile.location || '',
      bio: profile.bio || '',
      profilePicture: profile.avatar_url || '',
    };
    const localData = existing.data || {};
    const mergedData = apiWins
      ? { ...localData, ...apiData }
      : Object.keys(apiData).reduce((acc, key) => {
          acc[key] = localData[key] || apiData[key];
          return acc;
        }, {});
    const merged = {
      currentStep: existing.currentStep ?? 0,
      data: mergedData,
    };
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(merged));
  } catch {}
}

function* getCommunityUid() {
  let uid = localStorage.getItem(COMMUNITY_UID_KEY);
  if (!uid) {
    const response = yield call(getCommunityProfileRequest);
    const profile = response?.data?.data;
    syncProfileToStorage(profile);
    uid = profile?.uid ?? null;
  }
  return uid;
}

function* fetchCommunityProfile() {
  try {
    yield put(communitySetIsLoading(true));
    const response = yield call(getCommunityProfileRequest);
    const profile = response?.data?.data;
    syncProfileToStorage(profile);
    yield put(fetchCommunityProfileSuccess(profile));
  } catch (error) {
    yield put(fetchCommunityProfileFailure(error?.message));
  } finally {
    yield put(communitySetIsLoading(false));
  }
}

function* updateCommunityProfile({ payload }) {
  try {
    yield put(communitySetIsLoading(true));
    const response = yield call(updateProfileApi, payload);
    const profile = response?.data?.data;
    syncProfileToStorage(profile, true);
    yield put(updateCommunityProfileSuccess(profile));
    if (profile?.is_profile_complete) {
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: SUCCESS_MESSAGES.COMMUNITY_PROFILE_COMPLETE,
      });
    }
  } catch (error) {
    yield put(updateCommunityProfileFailure(error?.message));
  } finally {
    yield put(communitySetIsLoading(false));
  }
}

function dataUrlToFile(dataUrl, filename) {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

function* updateCommunityProfilePic({ payload }) {
  try {
    yield put(communitySetIsLoading(true));
    const { filename, content } = payload;
    const file = dataUrlToFile(content, filename);
    yield call(uploadCommunityAvatarRequest, file);
    yield put(fetchCommunityProfileRequest());
  } catch (error) {
    yield put(updateCommunityProfileFailure(error?.message));
  } finally {
    yield put(communitySetIsLoading(false));
  }
}

function* fetchSuggestedGroups({ payload: { page } }) {
  try {
    const response = yield call(getGroupsRequest, {
      sort_by: 'membership_count',
      per_page: 3,
      page,
      joined: 0,
    });
    yield put(
      fetchSuggestedGroupsSuccess({
        data: response?.data?.data ?? [],
        meta: response?.data?.meta,
        page,
      }),
    );
  } catch (error) {
    yield put(fetchSuggestedGroupsFailure(error?.message));
  }
}

function* fetchMyGroups({ payload: { page } }) {
  try {
    const uid = yield* getCommunityUid();
    if (!uid) {
      yield put(fetchMyGroupsFailure('Community user ID not found'));
      return;
    }
    const response = yield call(getMemberGroupsRequest, uid, {
      per_page: 99,
      page,
    });
    const data = (response?.data?.data ?? [])
      .filter(item => item.group != null)
      .map(item => {
        const g = item.group;
        return {
          id: g.uuid,
          title: g.name,
          meta: `${g.members_count ?? 0} Members | ${g.posts_count ?? 0} Posts`,
          image: g.logo || null,
        };
      });
    yield put(
      fetchMyGroupsSuccess({
        data,
        meta: response?.data?.meta,
        page,
      }),
    );
  } catch (error) {
    yield put(fetchMyGroupsFailure(error?.message));
  }
}

function* notificationsFetch({ payload: { page } }) {
  try {
    const response = yield call(getNotificationsRequest, page, 10);
    yield put(
      fetchNotificationsSuccess({
        data: response?.data?.data ?? [],
        meta: response?.data?.meta,
        page,
      }),
    );
  } catch (error) {
    yield put(fetchNotificationsFailure(error?.message));
  }
}

function* notificationMarkRead({ payload: notificationId }) {
  try {
    yield call(markNotificationReadApiRequest, notificationId);
    yield put(updateNotificationRead(notificationId));
  } catch {}
}

function* notificationMarkAllRead() {
  try {
    yield call(markAllNotificationsReadApiRequest);
    yield put(updateAllNotificationsRead());
  } catch {}
}

function* notificationDelete({ payload: notificationId }) {
  try {
    yield call(deleteNotificationApiRequest, notificationId);
    yield put(removeNotification(notificationId));
  } catch {}
}

function* notificationDeleteAll() {
  try {
    yield call(deleteAllNotificationsApiRequest);
    yield put(clearAllNotifications());
  } catch {}
}

function* communitySaga() {
  yield takeLatest(FETCH_COMMUNITY_PROFILE_REQUEST, fetchCommunityProfile);
  yield takeLatest(UPDATE_COMMUNITY_PROFILE_REQUEST, updateCommunityProfile);
  yield takeLatest(
    UPDATE_COMMUNITY_PROFILE_PIC_REQUEST,
    updateCommunityProfilePic,
  );
  yield takeEvery(FETCH_SUGGESTED_GROUPS_REQUEST, fetchSuggestedGroups);
  yield takeEvery(FETCH_MY_GROUPS_REQUEST, fetchMyGroups);
  yield takeEvery(FETCH_NOTIFICATIONS_REQUEST, notificationsFetch);
  yield takeLatest(MARK_NOTIFICATION_READ_REQUEST, notificationMarkRead);
  yield takeLatest(
    MARK_ALL_NOTIFICATIONS_READ_REQUEST,
    notificationMarkAllRead,
  );
  yield takeLatest(DELETE_NOTIFICATION_REQUEST, notificationDelete);
  yield takeLatest(DELETE_ALL_NOTIFICATIONS_REQUEST, notificationDeleteAll);
}

export default communitySaga;
